import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/features/auth/contexts/AuthContext';

export interface PostCareTemplate {
  id: string;
  title: string;
  service_type: string;
  content: string;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

const SERVICE_TYPES = ['tattoo', 'piercing', 'microblading', 'dermographie', 'laser', 'autre'] as const;
export type ServiceType = typeof SERVICE_TYPES[number];
export { SERVICE_TYPES };

export function usePostCare() {
  const { user } = useAuth();
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ['post-care', user?.id],
    enabled: !!user,
    queryFn: async (): Promise<PostCareTemplate[]> => {
      const { data, error } = await supabase
        .from('post_care_templates')
        .select('*')
        .eq('owner_id', user!.id)
        .order('service_type')
        .order('is_default', { ascending: false })
        .order('updated_at', { ascending: false });
      if (error) throw error;
      return (data || []) as PostCareTemplate[];
    },
  });

  const createTemplate = useMutation({
    mutationFn: async (data: { title: string; content: string; service_type: string; is_default?: boolean }) => {
      const { error } = await supabase.from('post_care_templates').insert({ owner_id: user!.id, ...data });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['post-care'] }),
  });

  const updateTemplate = useMutation({
    mutationFn: async (data: { id: string; title?: string; content?: string; service_type?: string; is_default?: boolean }) => {
      const { id, ...updates } = data;
      const { error } = await supabase.from('post_care_templates').update(updates).eq('id', id).eq('owner_id', user!.id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['post-care'] }),
  });

  const deleteTemplate = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('post_care_templates').delete().eq('id', id).eq('owner_id', user!.id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['post-care'] }),
  });

  return { ...query, templates: query.data || [], createTemplate, updateTemplate, deleteTemplate };
}
